import { createAsyncThunk, createSlice, PayloadAction, createDraftSafeSelector } from '@reduxjs/toolkit';
import axios from 'axios';
import { ELoadingStatus } from "./Enums";
import { AxiosErrorHandler, AxiosErrorHandlerX, } from "../Framework/AxiosHelper/AxiosHelper";
import { post_encrypt, getKey } from "../components/MainLibrary";
import { RootState } from "../store";
import CryptoJS from 'crypto-js';


import {
  IJamHawState, 
  ICredentials,
  IAttendee,
  IImage,
  IAttendeeResponse,
  IMerchandise,
  IMerchandiseResponse,
  ISaturdayVote,
  IAuth,
  IUP,
  IEncryptedPayload,
  } from './JamHawInterface';

//const URL = process.env.REACT_APP_BASE_URL;

const auth_user  = String(process.env.REACT_APP_AUTH_USER);
const auth_pswd = String(process.env.REACT_APP_AUTH_PSWD);
const secretJamHawAPIKey = String(process.env.REACT_APP_JAMHAW_KEY);

const LOCATION = window.location.toString().split("/")[2];
const MAIN_URL = LOCATION==="localhost:3031" ? "https://localhost:44333" : "https://stiletto.ddns.net/JamHawNetCoreApi";
//const MAIN_URL = "https://stiletto.ddns.net/JamHawNetCoreApi";

// console.log("URL", URL);
// console.log("MAIN_URL", MAIN_URL);
// console.log(auth_user, auth_pswd);

//const URL = "http://localhost:59233"
export interface IDialogManager {
  classx? : any;
  title: string;
  message: string; 
  button1: string; 
  button2?: string;
  button3?: string;
  exceptionType?: string;
  viewType?: string;
  data? : any;
}

///////////////////////////////////////////////////////////////////////////////////////
// Derive AES-256 key from shared secret using PBKDF2
async function deriveKey(secret: string): Promise<CryptoKey> {
  const enc = new TextEncoder();

  const keyMaterial = await crypto.subtle.importKey(
    "raw",
    enc.encode(secret),
    { name: "PBKDF2" },
    false,
    ["deriveKey"]
  );

  return crypto.subtle.deriveKey(
    {
      name: "PBKDF2",
      salt: enc.encode("jamhaw-auth"), // MUST match server
      iterations: 100_000,
      hash: "SHA-256"
    },
    keyMaterial,
    {
      name: "AES-GCM",
      length: 256
    },
    false,
    ["encrypt", "decrypt"]
  );
}


/////////////////////////////////////////////////////////////////////////////////////// toBase64
// Convert Uint8Array to Base64
function toBase64(bytes: Uint8Array): string {
  let binary = "";
  const chunkSize = 0x8000;

  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
  }

  return btoa(binary);
}

//////////////////////////////////////////////////////////////////////////////////////// encryptAuthPayload
// Encrypt object using AES-256-GCM
async function encryptAuthPayload<T extends object>(
  payload: T,
  secret: string
): Promise<IEncryptedPayload> {

  const enc = new TextEncoder();
  const key = await deriveKey(secret);

  // 12 bytes for GCM (REQUIRED)
  const nonce = crypto.getRandomValues(new Uint8Array(12));

  const plaintext = enc.encode(JSON.stringify(payload));

  const encryptedBuffer = await crypto.subtle.encrypt(
    {
      name: "AES-GCM",
      iv: nonce
    },
    key,
    plaintext
  );

  const encryptedBytes = new Uint8Array(encryptedBuffer);

  // WebCrypto appends 16 byte tag to end
  const tagLength = 16;
  const ciphertext = encryptedBytes.slice(0, encryptedBytes.length - tagLength);
  const tag = encryptedBytes.slice(encryptedBytes.length - tagLength);

  return {
    data: toBase64(ciphertext),
    nonce: toBase64(nonce),
    tag: toBase64(tag)
  };
}

/////////////////////////////////////////////////////////////////////////////////////// AUTH
export const getAuthorizeXThunk = createAsyncThunk< any, void, { rejectValue: string }>(
  "sms/authsolx",
  async (_, thunkAPI) => {
    try {
      const payload = {
        Username: auth_user,
        Password: auth_pswd
      };

      // 🔐 Encrypt (must be awaited)
      const encryptedPayload = await encryptAuthPayload(
        payload,
        secretJamHawAPIKey
      );

      const response = await axios.post(
        `${MAIN_URL}/auth/login`,
        {
          data: encryptedPayload.data,
          nonce: encryptedPayload.nonce,
          tag: encryptedPayload.tag
        },
        {
          headers: {
            "Content-Type": "application/json"
          }
        }
      );


      return await response.data;
    } catch (error: unknown) {
      return thunkAPI.rejectWithValue(AxiosErrorHandlerX(error).message);
    }
  }
);


///////////////////////////////////////////////////////////////////////
// SATURDAY
///////////////////////////////////////////////////////////////////////

export const getSaturdaysThunk = createAsyncThunk(
  "jamhaw/saturdays", async (args:ISaturdayVote, thunkAPI) => {

  var payload = await post_encrypt(JSON.stringify( args ),"jamhaw-2026");
  let argx:any ={ data: payload};

  try {
        const response = await axios.post(`${MAIN_URL}/jamhaw/saturdays`, 
          JSON.stringify( argx ),
          {
            headers: { 
              'Authorization': `Bearer ${args.token}`,
              //"ACCEPT": "POST, OPTIONS",
              //"access-control-allow-origin": "*",
              "content-type": "application/json",
            },
            timeout: 10000,

          });
          return await response.data; 
        } catch (error: any) {
          return thunkAPI.rejectWithValue(AxiosErrorHandler(error));
        }
  }
)

///////////////////////////////////////////////////////////////////////
// SATURDAY
///////////////////////////////////////////////////////////////////////

export const getSaturdayVotesThunk = createAsyncThunk(
  "jamhaw/saturdayvotes", async (args:ISaturdayVote, thunkAPI) => {

  var payload = await post_encrypt(JSON.stringify( args ),"jamhaw-2026");
  let argx:any ={ data: payload};
    
  try {
        const response = await axios.post(`${MAIN_URL}/jamhaw/saturday_votes`, 
          JSON.stringify( argx ),
          {
            headers: { 
              'Authorization': `Bearer ${args.token}`,
              //"ACCEPT": "POST, OPTIONS",
              //"access-control-allow-origin": "*",
              "content-type": "application/json",
            },
            timeout: 10000,

          });
          return await response.data; 
        } catch (error: any) {
          return thunkAPI.rejectWithValue(AxiosErrorHandler(error));
        }
  }
)

///////////////////////////////////////////////////////////////////////
// ATTENDEES
///////////////////////////////////////////////////////////////////////

export const getAttendeesThunk = createAsyncThunk(
  "jamhaw/attendees", async (token:string, thunkAPI) => {

  try {
        const response = await axios.get(`${MAIN_URL}/jamhaw/attendees`, 
          {
            headers: { 
              'Authorization': `Bearer ${token}`,
              //"ACCEPT": "GET, OPTIONS",
              //"access-control-allow-origin": "*",
              "content-type": "application/json",
            },
            timeout: 10000,

          });
          return await response.data;
        } catch (error: any) {
          return thunkAPI.rejectWithValue(AxiosErrorHandler(error));
        }
  }
)

export const updateAttendeeCountThunk = createAsyncThunk(
  "jamhaw/update_attendees", async (args:IAttendee, thunkAPI) => {

  var payload = await post_encrypt(JSON.stringify( args ),"jamhaw-2026");
  let argx:any ={ data: payload};

  try {
        const response = await axios.post(`${MAIN_URL}/jamhaw/update_attendees`, 
          JSON.stringify( argx ),
          {
            headers: { 
              'Authorization': `Bearer ${args.token}`,
              //"access-control-allow-origin": "*",
              //"ACCEPT": "GET, OPTIONS",
              "content-type": "application/json",
            },
            timeout: 10000,

          });
          return await response.data; 
        } catch (error: any) {
          return thunkAPI.rejectWithValue(AxiosErrorHandler(error));
        }
  }
)

export const updateMerchOrderThunk = createAsyncThunk(
  "jamhaw/update_merch", async (args:IMerchandise, thunkAPI) => {

  var payload = await post_encrypt(JSON.stringify( args ),"jamhaw-2026");
  let argx:any ={ data: payload};

  try {
        const response = await axios.post(`${MAIN_URL}/jamhaw/update_merchandise`, 
          JSON.stringify( argx ),
          {
            headers: { 
              'Authorization': `Bearer ${args.token}`,
              //"access-control-allow-origin": "*",
             // "ACCEPT": "GET, OPTIONS",
              "content-type": "application/json",
            },
            timeout: 10000,

          });
          getAttendeesThunk(args.token);
          return await response.data;
      } catch (error: any) {
        return thunkAPI.rejectWithValue(AxiosErrorHandler(error));
      }
  }
)

export const getMerchOrderThunk = createAsyncThunk(
  "jamhaw/getmerch", async (token:string, thunkAPI) => {

  try {
        const response = await axios.get(`${MAIN_URL}/jamhaw/merchandise`, 
          {
            headers: { 
              'Authorization': `Bearer ${token}`,
              //"access-control-allow-origin": "*",
              //"ACCEPT": "GET, OPTIONS",
              "content-type": "application/json",
            },
            timeout: 10000,

          });
          return await response.data;
      } catch (error: any) {
        return thunkAPI.rejectWithValue(AxiosErrorHandler(error));
      }
  }
)

export const validateThunk = createAsyncThunk(
  "jamhaw/validation", async (args:ICredentials, thunkAPI) => {

  var payload = await post_encrypt(JSON.stringify( args ),"jamhaw-2026");
  let argx:any ={ data: payload};

  try {
        const response = await axios.post(`${MAIN_URL}/jamhaw/validation`, 
          JSON.stringify( argx ),
          {
            headers: { 
              'Authorization': `Bearer ${args.token}`,
              //"access-control-allow-origin": "*",
              //"ACCEPT": "GET, OPTIONS",
              "content-type": "application/json",
            },
            timeout: 10000,

          });
          return await response.data;
      } catch (error: any) {
        return thunkAPI.rejectWithValue(AxiosErrorHandler(error));
      }
  }
)

////////////////////////////////////////////////////
const initialState: IJamHawState = {
  saturday_vote_loading: ELoadingStatus.idle,
  attendees_loading: ELoadingStatus.idle,
  attendees_response_loading: ELoadingStatus.idle,  
  validate_loading: ELoadingStatus.idle,    
  merchandise_loading: ELoadingStatus.idle,  
  merchandise_response_loading: ELoadingStatus.idle,  
  auth_loading : ELoadingStatus.idle,  

  error_validate_msg: "",
  
  error_attendees_msg: "",
  error_attendees_response_msg:"",

  error_merchandise_msg: "",
  error_merchandise_response_msg:"",

  auth: {
    token: ""
  },

  saturdays: {
    saturdays: [],
    email: "",
    mode: "test",
    token: "",
  },

  isOK: false,
  isCANCEL: false,  
  showDialog: false,
  loggedIn: false,
  gdpr: false,
  credentials: {
    userName : "", 
    passWord: "",
    groupName: "",
    token: "",
  },
  galleryMode: false,
  showMenu: true,
  showSidebar: true,
  showFooter: true,
  view: "HOME_VIEW",
  dialogMessage: {  
    classx : "",
    title: "",
    message: "",
    button1: "",
    button2: "",
    exceptionType: "",
  } as IDialogManager,  
  attendees: [],
  gallery: [],
  merchandise: [],
  validate: {response:"", validation:""},
  attendees_response: {attendee: "", response:""},
  merchandise_response: {response: ""},
  passwordFail: false,
  emailFail: false,
}

///////////////////////////////////////////////////
export const JamHawSlice = createSlice({
  name: 'jamhaw',
  initialState,
  reducers: {

      ResetLoadingStatus: (state) =>{
          state.attendees_loading = ELoadingStatus.idle;
          state.validate_loading = ELoadingStatus.idle;   
          state.attendees_response_loading = ELoadingStatus.idle;
          state.merchandise_loading = ELoadingStatus.idle;
          state.merchandise_response_loading = ELoadingStatus.idle;
          state.saturday_vote_loading = ELoadingStatus.idle;
          state.auth_loading = ELoadingStatus.idle;
      },
      UpdateDialogMessage: (state, action: PayloadAction<IDialogManager>) => {
        state.dialogMessage = action.payload;
      },
      ClearDialogMessage: (state) => {
        state.dialogMessage.classx = "";
        state.dialogMessage.title = "";
        state.dialogMessage.message = "";
        state.dialogMessage.button1 = "";
        state.dialogMessage.button2 = "";
        state.dialogMessage.exceptionType = "";
      },   
      UpdateShowDialog: (state, action: PayloadAction<boolean>) => {
        //console.log("UpdateShowDialog", action.payload);
        state.showDialog = action.payload;
      },    
      UpdateIsOK: (state, action: PayloadAction<boolean>) => {
        state.isOK = action.payload;
      },      
      UpdateIsCANCEL: (state, action: PayloadAction<boolean>) => {
        state.isCANCEL = action.payload;
      },      
      UpdateView: (state, action: PayloadAction<string>) => {
        state.view = "BLANK";
        state.view = action.payload;
      },      
      UpdateLoggedIn: (state, action: PayloadAction<boolean>) => {
        state.loggedIn = action.payload;
      },     
      UpdateGDPR: (state, action: PayloadAction<boolean>) => {
        state.gdpr = action.payload;
      },         
      UpdateGalleryMode: (state, action: PayloadAction<boolean>) => {
        state.galleryMode = action.payload;
      },      
      UpdateShowMenu: (state, action: PayloadAction<boolean>) => {
        state.showMenu  = action.payload;
      },  
      UpdateShowSidebar: (state, action: PayloadAction<boolean>) => {
        state.showSidebar  = action.payload;
      },  
      UpdateShowFooter: (state, action: PayloadAction<boolean>) => {
        state.showFooter  = action.payload;
      },  
      UpdateCredentials: (state, action: PayloadAction<ICredentials>) => {
        state.credentials = action.payload;
      }, 
      UpdateAttendees: (state, action: PayloadAction<IAttendee[]>) => {
        state.attendees = action.payload;
      }, 
      UpdateGallery: (state, action: PayloadAction<IImage[]>) => {
        state.gallery = action.payload;
      },       
      UpdatePasswordFail: (state, action: PayloadAction<boolean>) => {
        state.passwordFail = action.payload;
      },     
      UpdateEmailFail: (state, action: PayloadAction<boolean>) => {
        state.emailFail = action.payload;
      },           
  
  },
  extraReducers: (builder) => {

      /////////////////////////////////////////////////////////////////////////////////////
      /** ATTENDEES **/
      builder.addCase(getAttendeesThunk.pending, (state) => {
        state.attendees = [];
        state.attendees_loading = ELoadingStatus.loading;
      });

      builder.addCase(getAttendeesThunk.fulfilled, (state, { payload }) => {
        //console.log(">>>",payload);
        state.attendees = payload.attendees;
        state.attendees_loading = ELoadingStatus.loaded;
      });
      
      builder.addCase(getAttendeesThunk.rejected,(state, action) => {
        state.attendees_loading = ELoadingStatus.error;
      });      

      /////////////////////////////////////////////////////////////////////////////////////
      /** ATTENDEES UPDATE **/
      builder.addCase(updateAttendeeCountThunk.pending, (state) => {
        state.attendees_response_loading = ELoadingStatus.loading;
      });

      builder.addCase(updateAttendeeCountThunk.fulfilled, (state, { payload }) => {
        state.attendees_response = payload;
        state.attendees_response_loading = ELoadingStatus.loaded;

      });
      
      builder.addCase(updateAttendeeCountThunk.rejected,(state, action) => {
        state.attendees_loading = ELoadingStatus.error;
      }); 

      /////////////////////////////////////////////////////////////////////////////////////
      /** VALIDATE **/
      builder.addCase(validateThunk.pending, (state) => {
        state.validate_loading = ELoadingStatus.loading;
      });

      builder.addCase(validateThunk.fulfilled, (state, { payload }) => {
        state.validate = payload;
        state.validate_loading = ELoadingStatus.loaded;
      });
      
      builder.addCase(validateThunk.rejected,(state, action) => {
        state.validate_loading = ELoadingStatus.error;
        console.log(action.error);
      });        

      /////////////////////////////////////////////////////////////////////////////////////
      /** GET MERCHANDISE **/
      builder.addCase(getMerchOrderThunk.pending, (state) => {
        state.merchandise_loading = ELoadingStatus.loading;
      });

      builder.addCase(getMerchOrderThunk.fulfilled, (state, { payload }) => {
        state.merchandise = payload.merchandise;
        state.merchandise_loading = ELoadingStatus.loaded;
      });
      
      builder.addCase(getMerchOrderThunk.rejected,(state, action) => {
        state.merchandise_loading = ELoadingStatus.error;
      });  

      /////////////////////////////////////////////////////////////////////////////////////
      /** MERCHANDISE UPDATE **/
      builder.addCase(updateMerchOrderThunk.pending, (state) => {
        state.merchandise_response_loading = ELoadingStatus.loading;
      });

      builder.addCase(updateMerchOrderThunk.fulfilled, (state, { payload }) => {
        state.merchandise_response = payload;
        state.merchandise_response_loading = ELoadingStatus.loaded;
      });
      
      builder.addCase(updateMerchOrderThunk.rejected,(state, action) => {
        state.merchandise_response_loading = ELoadingStatus.error;
      });       

      /////////////////////////////////////////////////////////////////////////////////////
      /** SATURDAY VOTE **/
      builder.addCase(getSaturdayVotesThunk.pending, (state) => {
        state.saturday_vote_loading = ELoadingStatus.loading;
      });

      builder.addCase(getSaturdayVotesThunk.fulfilled, (state, { payload }) => {
        state.saturdays = payload;
        state.saturday_vote_loading = ELoadingStatus.loaded;
      });
      
      builder.addCase(getSaturdayVotesThunk.rejected,(state, action) => {
        state.saturday_vote_loading = ELoadingStatus.error;
      });       

      /////////////////////////////////////////////////////////////////////////////////////
      /** SATURDAYS **/
      builder.addCase(getSaturdaysThunk.pending, (state) => {
        state.saturday_vote_loading = ELoadingStatus.loading;
      });

      builder.addCase(getSaturdaysThunk.fulfilled, (state, { payload }) => {
        state.saturdays = payload;
        state.saturday_vote_loading = ELoadingStatus.loaded;
      });
      
      builder.addCase(getSaturdaysThunk.rejected,(state, action) => {
        state.saturday_vote_loading = ELoadingStatus.error;
      });       

    ///////////////////////////////////////////////////////
    // TOKEN AUTHORIZATION
    // builder.addCase(getAuthorizeThunk.pending, (state: any, action) => {
    //     state.wait_cursor = true;
    //     state.auth = null;
    //     state.auth_loading = ELoadingStatus.loading;
    // })
    // .addCase(getAuthorizeThunk.fulfilled, (state: any, { payload }) => {
    //     state.auth = payload;
    //     state.wait_cursor = false;
    //     state.auth_loading = ELoadingStatus.loaded;
    // })
    //     .addCase(getAuthorizeThunk.rejected, (state: any, action) => {
    //     state.wait_cursor = false;
    //     state.auth_loading = ELoadingStatus.error;
    // })
      

    ///////////////////////////////////////////////////////
    // TOKEN AES-256 AUTHORIZATION
    builder.addCase(getAuthorizeXThunk.pending, (state: any, action) => {
      state.wait_cursor = true;
      state.auth = null;
      state.auth_loading = ELoadingStatus.loading;
    })
    .addCase(getAuthorizeXThunk.fulfilled, (state: any, { payload }) => {
      state.auth = payload;
      state.wait_cursor = false;
      state.auth_loading = ELoadingStatus.loaded;
    })
    .addCase(getAuthorizeXThunk.rejected, (state: any, action) => {
      state.wait_cursor = false;
      state.auth_loading = ELoadingStatus.error;
    })    
    }
  },
)


export const {
  UpdateEmailFail,
  UpdatePasswordFail,
  ResetLoadingStatus,
  UpdateGallery,
  UpdateAttendees,
  UpdateCredentials,
  UpdateShowFooter,
  UpdateShowMenu,
  UpdateShowSidebar,
  UpdateGalleryMode,
  UpdateView,
  UpdateLoggedIn,
  UpdateGDPR,
  UpdateDialogMessage,
  ClearDialogMessage,
  UpdateShowDialog,
  UpdateIsOK,
  UpdateIsCANCEL,
} = JamHawSlice.actions;

//const selectSelf = (state: any) => state

export const selectIsOK           = (rootState: RootState) => { return rootState.jamhaw.isOK; };
export const selectIsCANCEL       = (rootState: RootState) => { return rootState.jamhaw.isCANCEL; };
export const selectDialogMessage  = (rootState: RootState) => { return rootState.jamhaw.dialogMessage; };
export const selectShowDialog     = (rootState: RootState) => { return rootState.jamhaw.showDialog; };
export const selectView           = (rootState: RootState) => { return rootState.jamhaw.view; };
export const selectLoggedIn       = (rootState: RootState) => { return rootState.jamhaw.loggedIn; };
export const selectGalleryMode    = (rootState: RootState) => { return rootState.jamhaw.galleryMode; };
export const selectShowMenu       = (rootState: RootState) => { return rootState.jamhaw.showMenu; };
export const selectShowSidebar    = (rootState: RootState) => { return rootState.jamhaw.showSidebar; };
export const selectShowFooter     = (rootState: RootState) => { return rootState.jamhaw.showFooter; };
export const selectCredentials    = (rootState: RootState) => { return rootState.jamhaw.credentials ; };
export const selectPasswordFail   = (rootState: RootState) => { return rootState.jamhaw.passwordFail; };
export const selectEmailFail      = (rootState: RootState) => { return rootState.jamhaw.emailFail; };
export const selectGDPR           = (rootState: RootState) => { return rootState.jamhaw.gdpr; };

export const selectGallery              = (rootState: RootState) => { return rootState.jamhaw.gallery ; };
export const selectValidate             = (rootState: RootState) => { return rootState.jamhaw.validate; };
export const selectAttendees            = (rootState: RootState) => { return rootState.jamhaw.attendees ; };
export const selectAttendeesResponse    = (rootState: RootState) => { return rootState.jamhaw.attendees_response ; };
export const selectMerchandise          = (rootState: RootState) => { return rootState.jamhaw.merchandise ; };
export const selectMerchandiseResponse  = (rootState: RootState) => { return rootState.jamhaw.merchandise_response ; };
export const selectSaturdays            = (rootState: RootState) => { return rootState.jamhaw.saturdays ; };
export const selectAuth                 = (rootState: RootState) => { return rootState.jamhaw.auth as IAuth;
};


/* STATUS */
export const selectAttendeesStatus            = (rootState: RootState) => { return rootState.jamhaw.attendees_loading; };
export const selectValidateStatus             = (rootState: RootState) => { return rootState.jamhaw.validate_loading; };
export const selectAttendeesResponseStatus    = (rootState: RootState) => { return rootState.jamhaw.attendees_response_loading; };
export const selectMerchandiseStatus          = (rootState: RootState) => { return rootState.jamhaw.merchandise_loading; };
export const selectMerchandiseResponseStatus  = (rootState: RootState) => { return rootState.jamhaw.merchandise_response_loading; };
export const selectSaturdayVoteStatus         = (rootState: RootState) => { return rootState.jamhaw.saturday_vote_loading; };
export const selectLoadingAuthStatus          = (rootState: RootState) => { return rootState.jamhaw.auth_loading;};

/* ERRORS */
// export const selectValidateMsg            = createDraftSafeSelector(  selectSelf,  (state) => state.jamhaw.error_validate_msg);
// export const selectAttendeesMsg           = createDraftSafeSelector(  selectSelf,  (state) => state.jamhaw.error_attendees_msg);
// export const selectAttendeesResponseMsg   = createDraftSafeSelector(  selectSelf,  (state) => state.jamhaw.error_attendees_response_msg);
// export const selectMerchandiseMsg         = createDraftSafeSelector(  selectSelf,  (state) => state.jamhaw.error_Merchandise_msg);
// export const selectMerchandiseResponseMsg = createDraftSafeSelector(  selectSelf,  (state) => state.jamhaw.error_Merchandise_msg);




export default JamHawSlice.reducer